import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Button, TextField, CircularProgress, Typography,
  Link, Box, Alert, InputAdornment, IconButton, LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useLocation, useHistory } from "react-router-dom";
import { resetPassword } from "../../services/AuthentificationService";
import { motion, AnimatePresence } from "framer-motion";
import AuthLayout, { itemVariants } from "./AuthLayout";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/SecurityOutlined";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.88)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.96)",
      "& fieldset": { borderColor: "rgba(255,255,255,0.8)" },
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 4px rgba(255,255,255,0.16), 0 12px 26px rgba(2,29,71,0.18)",
      "& fieldset": { borderColor: "#03407b", borderWidth: 2 },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#03407b" },
}));

const UpdateButton = styled(Button)(() => ({
  borderRadius: 12,
  padding: "13px 0",
  fontSize: "1rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  background: "linear-gradient(135deg, #03407b 0%, #02357a 100%)",
  boxShadow: "0 4px 15px rgba(99,102,241,0.4)",
  transition: "all 0.25s ease",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #02357a 0%, #022d68 100%)",
    boxShadow: "0 6px 20px rgba(99,102,241,0.5)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#e2e8f0", boxShadow: "none", color: "#94a3b8" },
}));

function getStrength(pwd) {
  if (!pwd) return { score: 0, label: "", color: "#e2e8f0" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const map = [
    { label: "", color: "#e2e8f0" },
    { label: "Faible", color: "#ef4444" },
    { label: "Moyen", color: "#f59e0b" },
    { label: "Bon", color: "#10b981" },
    { label: "Fort", color: "#03407b" },
  ];
  return { score, ...map[score] };
}

const BrandContent = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
    <Box sx={{ mb: 4 }}>
      <img
        src={process.env.REACT_APP_LOGO_URL}
        alt="Logo"
        style={{ width: 170, filter: "brightness(0) invert(1)" }}
      />
    </Box>

    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box sx={{
        width: 100, height: 100, borderRadius: "28px",
        background: "rgba(255,255,255,0.18)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        mx: "auto", mb: 4,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      }}>
        <ShieldOutlinedIcon sx={{ fontSize: 52, color: "#fff" }} />
      </Box>
    </motion.div>

    <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, mb: 2, fontSize: "1.8rem" }}>
      Sécurisez votre compte
    </Typography>
    <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "1rem", maxWidth: 300, mx: "auto", lineHeight: 1.6 }}>
      Choisissez un mot de passe fort pour protéger votre accès.
    </Typography>

  </Box>
);

const SuccessView = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: "20px",
        background: "linear-gradient(135deg, #10b981, #059669)",
        display: "flex", alignItems: "center", justifyContent: "center",
        mx: "auto", mb: 3,
        boxShadow: "0 8px 24px rgba(16,185,129,0.35)",
      }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#fff" }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: "#ffffff", mb: 1, textShadow: "0 8px 18px rgba(2, 29, 71, 0.22)" }}>
        Mot de passe mis à jour !
      </Typography>
      <Typography sx={{ color: "#cdeeff", fontSize: "1rem", mb: 4, fontWeight: 600 }}>
        Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
      </Typography>

      <Link
        component={RouterLink}
        to="/login"
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75,
          color: "#fff", fontWeight: 600, fontSize: "1rem",
          textDecoration: "none", px: 3, py: 1.5,
          background: "linear-gradient(135deg, #0556a4, #03407b)",
          borderRadius: 3,
          boxShadow: "0 4px 15px rgba(99,102,241,0.4)",
          transition: "all 0.2s",
          "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(99,102,241,0.5)" },
        }}
      >
        Se connecter
      </Link>
    </Box>
  </motion.div>
);

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword({ snackbarShowMessage }) {
  const mounted = useRef(true);
  const history = useHistory();
  const query = useQuery();
  const token = query.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [done, setDone] = useState(false);

  const strength = getStrength(password);

  useEffect(() => () => { mounted.current = false; }, []);

  const validate = () => {
    const temp = {};
    temp.password = password ? "" : "Le nouveau mot de passe est obligatoire.";
    temp.confirmPassword = confirmPassword ? "" : "La confirmation est obligatoire.";
    if (password && confirmPassword && password !== confirmPassword)
      temp.confirmPassword = "Les mots de passe ne correspondent pas.";
    if (!token) temp.token = "Lien invalide (token manquant).";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!validate()) return;

    setLoading(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
      snackbarShowMessage("Mot de passe mis à jour avec succès.", "success", 5000);
    } catch (err) {
      const msg = err.response?.data?.message || "Lien invalide ou expiré.";
      setApiError(msg);
      snackbarShowMessage(msg, "error", 5000);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <AuthLayout brandContent={<BrandContent />}>
      <AnimatePresence mode="wait">
        {done ? (
          <SuccessView key="success" />
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            
            <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible">
              <Typography variant="h4" sx={{ fontWeight: 850, color: "#ffffff", mb: 0.75, fontSize: "1.45rem", textAlign: "center", textShadow: "0 8px 18px rgba(2, 29, 71, 0.22)" }}>
                Nouveau mot de passe
              </Typography>
              <Typography sx={{ color: "#cdeeff", fontSize: "1rem", mb: 4, textAlign: "center", fontWeight: 500 }}>
                Choisissez un mot de passe sécurisé pour votre compte.
              </Typography>
            </motion.div>

            
            {!token && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
                Lien invalide ou expiré. Veuillez recommencer la procédure.
              </Alert>
            )}

            
            <AnimatePresence>
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setApiError("")}>
                    {apiError}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Box component="form" noValidate onSubmit={handleSubmit}>

              
              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible">
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#f4fbff", mb: 0.75, letterSpacing: "0.01em", textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)" }}>
                    NOUVEAU MOT DE PASSE
                  </Typography>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    placeholder="••••••••"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors((p) => ({ ...p, password: "" }));
                    }}
                    error={!!errors.password}
                    helperText={errors.password} slotProps={{ input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            sx={{ color: "#94a3b8", "&:hover": { color: "#03407b" } }}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    } }}
                  />
                </Box>

                
                {password && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.25 }}
                  >
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography sx={{ fontSize: "0.8125rem", color: "#d9f2ff", fontWeight: 600 }}>
                          Force du mot de passe
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: strength.color }}>
                          {strength.label}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(strength.score / 4) * 100}
                        sx={{
                          height: 5, borderRadius: 99,
                          backgroundColor: "#e2e8f0",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 99,
                            backgroundColor: strength.color,
                            transition: "all 0.3s ease",
                          },
                        }}
                      />
                    </Box>
                  </motion.div>
                )}
              </motion.div>

              
              <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible">
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#f4fbff", mb: 0.75, letterSpacing: "0.01em", textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)" }}>
                    CONFIRMER LE MOT DE PASSE
                  </Typography>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    placeholder="••••••••"
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: "" }));
                    }}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword} slotProps={{ input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm(!showConfirm)}
                            edge="end"
                            size="small"
                            sx={{ color: "#94a3b8", "&:hover": { color: "#03407b" } }}
                          >
                            {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    } }}
                  />
                </Box>
              </motion.div>

              
              <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible">
                <UpdateButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !token}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Mise à jour…</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ShieldOutlinedIcon fontSize="small" />
                      <span>Mettre à jour le mot de passe</span>
                    </Box>
                  )}
                </UpdateButton>
              </motion.div>

              
              <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible">
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      color: "#e3f7ff", fontWeight: 750, fontSize: "1rem",
                      textDecoration: "none",
                      textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)",
                      "&:hover": { color: "#ffffff", textDecoration: "underline" },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                    Retour à la connexion
                  </Link>
                </Box>
              </motion.div>
            </Box>

            
            <motion.div custom={5} variants={itemVariants} initial="hidden" animate="visible">
              <Typography sx={{ mt: 5, textAlign: "center", fontSize: "1rem", color: "#d9f2ff", fontWeight: 600 }}>
                © {new Date().getFullYear()} Vigor CRM · Tous droits réservés
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
